/**
 * NotificationBell (Trello #192) — the admin notification centre in the app bar.
 *
 * A bell with an unread badge; clicking opens a dropdown of recent platform-activity
 * alerts (new signups, pending approvals, org creation, same-domain clustering) with a
 * "mark all read" action. New alerts arriving live also surface as a transient toast.
 *
 * Data + the AppSync subscription live in useAdminAlerts; this component is presentation.
 */

import React, { useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { useAdminAlerts } from '../hooks/useAdminAlerts';
import type { AdminAlert, AdminAlertType } from '../services/api';

function iconForType(type: AdminAlertType): React.ReactElement {
  switch (type) {
    case 'new_user':
      return <PersonAddIcon fontSize="small" color="info" />;
    case 'pending_approval':
      return <PendingActionsIcon fontSize="small" color="warning" />;
    case 'domain_cluster':
      return <GroupsIcon fontSize="small" color="warning" />;
    case 'new_org':
      return <BusinessIcon fontSize="small" color="info" />;
    case 'trial_feedback':
      return <RateReviewIcon fontSize="small" color="info" />;
    // Trello #371 — an in-app idea / suggestion / bug report.
    case 'platform_feedback':
      return <RateReviewIcon fontSize="small" color="primary" />;
    case 'plan_upgraded':
      return <UpgradeIcon fontSize="small" color="success" />;
    // Trello #365 — revenue contraction, so both read as a warning against the
    // green upgrade above.
    case 'plan_downgraded':
      return <TrendingDownIcon fontSize="small" color="warning" />;
    case 'subscription_cancelled':
      return <CancelIcon fontSize="small" color="error" />;
    default:
      return <NotificationsIcon fontSize="small" />;
  }
}

/**
 * The admin page an alert should open when clicked, or null when there is
 * nowhere useful to go.
 *
 * Two layers, in precedence order:
 *
 * 1. A TYPE route, for alerts whose payload lives on a purpose-built page
 *    rather than on the entity. A trial-feedback alert is the live ping for a
 *    new review — clicking through takes the admin to the Feedback page where
 *    the written answers are readable. A platform-feedback alert (Trello #371)
 *    is the same idea for an in-app submission, landing on the same page's
 *    Ideas tab. The subscription movements (Trello #365) open the Subscription
 *    Movements tab, which shows the org, its previous package and the reason.
 *
 * 2. An ENTITY route, from the `entityType` + `entityId` every emitter already
 *    stamps on the alert (src/utils/admin-alerts/emit-admin-alert.ts). This is
 *    what makes an org run-limit warning, a new signup or a new organisation
 *    click straight through to the record the admin has to act on, instead of
 *    leaving them to search for it by name.
 *
 * `entityId` is the same identifier the list pages navigate with — the org's id
 * (`/organizations/:orgId`) and the user's `userId` (`/users/:userId`) — so no
 * lookup is needed. Alerts that carry an `entityType` but no `entityId` stay
 * inert: `domain_cluster` describes a SET of users, not one of them.
 */
export function routeForAlert(
  alert: Pick<AdminAlert, 'type' | 'entityId' | 'entityType'>,
): string | null {
  switch (alert.type) {
    case 'trial_feedback':
    case 'platform_feedback':
      return '/feedback';
    case 'plan_upgraded':
    case 'plan_downgraded':
    case 'subscription_cancelled':
      return '/subscription-movements';
    default:
      break;
  }

  if (!alert.entityId) return null;
  switch (alert.entityType) {
    case 'organization':
      return `/organizations/${encodeURIComponent(alert.entityId)}`;
    case 'user':
      return `/users/${encodeURIComponent(alert.entityId)}`;
    default:
      return null;
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const NotificationBell: React.FC = () => {
  const { alerts, unreadCount, liveAlert, markAllRead, dismissLiveAlert } = useAdminAlerts();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    markAllRead();
  };
  const handleClose = () => setAnchorEl(null);

  const goTo = (path: string) => {
    handleClose();
    dismissLiveAlert();
    navigate(path);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`} sx={{ mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {alerts.length > 0 && (
            <Button size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {alerts.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: 'auto' }}>
            {alerts.map((alert: AdminAlert) => {
              const target = routeForAlert(alert);
              return (
              <ListItem
                key={alert.id}
                alignItems="flex-start"
                divider
                onClick={target ? () => goTo(target) : undefined}
                sx={target ? { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } } : undefined}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>{iconForType(alert.type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {alert.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {relativeTime(alert.createdAt)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {alert.message}
                    </Typography>
                  }
                />
              </ListItem>
              );
            })}
          </List>
        )}
      </Popover>

      <Snackbar
        open={Boolean(liveAlert)}
        autoHideDuration={6000}
        onClose={dismissLiveAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {liveAlert ? (
          <Alert
            onClose={dismissLiveAlert}
            severity={liveAlert.severity === 'warning' ? 'warning' : 'info'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            <strong>{liveAlert.title}</strong> — {liveAlert.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default NotificationBell;
